/**
 * Public Parent Consent Page — Deskey DPDP Act 2023 (Verifiable Parental Consent)
 *
 * Route: /consent/:token        (no auth)
 *
 * Parent flow:
 *   1. Read agreement
 *   2. Confirm their name, phone number and email
 *   3. Confirm with the agreed checkbox + tap "I consent"
 *   4. Student account is auto-created on the backend
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Loader2, XCircle, Mail, MessageSquare, Smartphone } from 'lucide-react';
import { notifyError, friendlyError } from '../lib/errors';
import Loader from '../components/Loader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREEN = '#34A853';
const DARK = '#0C1A12';
const SOFT = '#F0FDF4';

const STATUS_BADGE = {
  pending:   { color: '#0EA5E9', label: 'Awaiting Approval' },
  verified:  { color: '#F59E0B', label: 'Awaiting Approval' },  // older consents
  approved:  { color: GREEN,     label: 'Consent Granted' },
  rejected:  { color: '#DC2626', label: 'Declined' },
  expired:   { color: '#6B7280', label: 'Expired' },
  revoked:   { color: '#6B7280', label: 'Revoked' },
};

export default function PublicConsentPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [consent, setConsent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Parent details (prefilled from the school's invite)
  const [parentName, setParentName] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [parentEmail, setParentEmail] = useState('');

  // Final consent
  const [agreed, setAgreed] = useState(false);
  const [approving, setApproving] = useState(false);
  const [final, setFinal] = useState(null);          // { ok, message, default_password }

  const reload = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await axios.get(`${API}/public/consent/${token}`);
      setConsent(data);
      setParentName(data.parent?.full_name || '');
      setParentEmail(data.parent?.email || '');
    } catch (e) {
      setError(friendlyError(e, 'Unable to load consent request'));
    }
    setLoading(false);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [token]);

  const detailsFilled = parentName.trim() && parentPhone.length === 10 && parentEmail.includes('@');
  const approved = consent?.status === 'approved';

  // ── Final approval ─────────────────────────────────────────────────────────
  const approve = async () => {
    if (!detailsFilled) { toast.error('Enter your name, phone number and email'); return; }
    if (!agreed) { toast.error('Please tick the agreement box'); return; }
    setApproving(true);
    try {
      const { data } = await axios.post(`${API}/public/consent/${token}/approve`, {
        agreement_version: consent.agreement_version, agreed: true,
        parent_full_name: parentName, parent_email: parentEmail, parent_mobile: parentPhone,
      });
      setFinal(data);
      toast.success('Consent recorded — student account activated');
    } catch (e) {
      notifyError(e, 'Approval failed');
    }
    setApproving(false);
  };

  const reject = async () => {
    const reason = window.prompt('Reason (optional)') || null;
    try {
      await axios.post(`${API}/public/consent/${token}/reject`, { reason });
      toast.success('Consent declined');
      reload();
    } catch (e) {
      notifyError(e, 'Failed to reject');
    }
  };

  if (loading) return <CenterLoader label="Loading parental consent…" />;
  if (error)   return <ErrorCard message={error} />;
  if (!consent) return null;

  const badge = STATUS_BADGE[consent.status] || STATUS_BADGE.pending;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: SOFT }} data-testid="public-consent-page">
      {/* Top brand strip */}
      <div className="w-full px-4 py-3 border-b border-emerald-100 bg-white">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold" style={{ background: GREEN }}>D</div>
            <div>
              <div className="text-sm font-bold" style={{ color: DARK, fontFamily: 'Space Grotesk, sans-serif' }}>Deskey</div>
              <div className="text-[10px] text-emerald-700 -mt-0.5 tracking-wider uppercase">DPDP Verifiable Parental Consent</div>
            </div>
          </div>
          <span className="text-[11px] font-bold px-3 py-1.5 rounded-full text-white" style={{ background: badge.color }} data-testid="consent-status-badge">
            {badge.label}
          </span>
        </div>
      </div>

      <div className="flex-1 max-w-5xl w-full mx-auto px-4 py-6 sm:py-10">
        {/* Already-approved success card */}
        {(approved || final) && (
          <div className="rounded-2xl bg-white border-2 border-emerald-200 p-7 shadow-lg" data-testid="consent-approved-card">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: SOFT }}>
                <CheckCircle2 className="w-6 h-6" style={{ color: GREEN }} />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold" style={{ color: DARK, fontFamily: 'Space Grotesk, sans-serif' }}>
                  Consent Recorded — {consent.student.full_name} is now active on Deskey
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  We've created an account for your child at <strong>{consent.institution_name}</strong>. They'll receive their login details by email.
                </p>
                {final?.default_password && (
                  <div className="mt-3 p-3 rounded-lg bg-emerald-50 border border-emerald-100 text-xs">
                    <strong>Default password:</strong>{' '}
                    <code className="font-mono bg-white px-1.5 py-0.5 rounded border border-emerald-100">{final.default_password}</code>{' '}
                    <span className="text-slate-500">— ask your child to change it on first login.</span>
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-3">
                  <strong>Future purchases:</strong> any Super-Credit purchase from this account will require your live OTP approval — even if you signed offline. This is your DPDP-mandated right as a guardian.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Live flow */}
        {!approved && !final && (
          <>
            {/* Intro card */}
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8" data-testid="consent-intro-card">
              <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: DARK, fontFamily: 'Space Grotesk, sans-serif' }}>
                Approve <span style={{ color: GREEN }}>{consent.student.full_name}'s</span> Deskey account
              </h1>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                Hi <strong>{consent.parent.full_name}</strong>, <strong>{consent.institution_name}</strong> has invited your child to Deskey. India's
                <strong> Digital Personal Data Protection Act, 2023 (Section 9)</strong> requires us to obtain your verifiable consent before activating any
                account for a child under 18.
              </p>

              <div className="mt-4 grid sm:grid-cols-3 gap-2.5 text-xs">
                <FactRow label="Student" value={`${consent.student.full_name} · Age ${consent.student.age}`} />
                <FactRow label="Institution" value={consent.institution_name} />
                <FactRow label="Your Relationship" value={consent.parent.relationship} />
              </div>
            </div>

            {/* Agreement card */}
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8 mt-5" data-testid="consent-agreement-card">
              <h2 className="text-base font-bold text-slate-900 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Agreement <span className="text-[10px] font-medium text-slate-500 ml-2">Version {consent.agreement_version}</span>
              </h2>
              <div className="text-[13px] text-slate-700 space-y-3 leading-relaxed border border-slate-100 rounded-xl p-4 bg-slate-50/60 max-h-72 overflow-y-auto" data-testid="dpdp-legal-text">
                <p>By approving, I confirm that:</p>
                <ol className="list-decimal pl-5 space-y-2">
                  <li>I am the <strong>lawful parent or legal guardian</strong> of {consent.student.full_name}, currently {consent.student.age} years old.</li>
                  <li>I authorise <strong>Deskey</strong> and <strong>{consent.institution_name}</strong> to create an account for my child, process
                    personal data strictly for educational purposes, and store it within India in compliance with the <strong>DPDP Act 2023</strong>.</li>
                  <li>I understand my rights under DPDP §11–14 — to access, correct, erase, and withdraw consent at any time by writing to
                    <strong> dpo@deskey.app</strong>.</li>
                  <li>I confirm my contact details (email & mobile) on file are correct; <strong>all future Super-Credit purchases by my child will require my live OTP approval</strong>, and only I will be the payer.</li>
                  <li>Deskey will not engage in <strong>behavioural advertising, profiling, or tracking</strong> of my child for commercial purposes (per DPDP §10).</li>
                  <li>I have read and accept Deskey's <a className="underline text-emerald-700" href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a> and <a className="underline text-emerald-700" href="/terms" target="_blank" rel="noreferrer">Terms of Service</a>.</li>
                </ol>
                <p className="text-[11px] text-slate-500 pt-2 border-t border-slate-200">
                  This consent record (including your IP, device, timestamp, and verification method) is stored as an
                  immutable audit log — your "verifiable" consent under the DPDP Act.
                </p>
              </div>
            </div>

            {/* Parent details */}
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm mt-5 p-6" data-testid="consent-parent-details-card">
              <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Your details</h2>
              <p className="text-xs text-slate-500 mt-0.5 mb-4">Confirm your name, phone number and email.</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <label htmlFor="parent-name" className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Full name</label>
                  <Input id="parent-name" value={parentName} onChange={(e) => setParentName(e.target.value)}
                         data-testid="parent-name-input" className="h-10 rounded-lg border-slate-200" />
                </div>
                <div>
                  <label htmlFor="parent-phone" className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Phone number</label>
                  <Input id="parent-phone" type="tel" inputMode="numeric" placeholder="10-digit mobile" value={parentPhone}
                         onChange={(e) => setParentPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                         data-testid="parent-phone-input" className="h-10 rounded-lg border-slate-200" />
                </div>
                <div>
                  <label htmlFor="parent-email" className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Email</label>
                  <Input id="parent-email" type="email" value={parentEmail} onChange={(e) => setParentEmail(e.target.value)}
                         data-testid="parent-email-input" className="h-10 rounded-lg border-slate-200" />
                </div>
              </div>
            </div>

            {/* Final consent */}
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm mt-5 p-6 sm:p-8" data-testid="consent-final-card">
              <div className="flex items-start gap-3 mb-4">
                <input type="checkbox" id="dpdp-agree" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="consent-agreed-checkbox"
                       className="mt-1 w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                <label htmlFor="dpdp-agree" className="text-sm text-slate-700 leading-relaxed cursor-pointer">
                  <strong>I, {consent.parent.full_name},</strong> being the {consent.parent.relationship} of {consent.student.full_name}, confirm I have
                  read the agreement above and grant <strong>verifiable parental consent</strong> for Deskey to process my child's personal data
                  for educational purposes under the DPDP Act 2023.
                </label>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={approve} disabled={approving || !detailsFilled || !agreed}
                        data-testid="consent-approve-btn"
                        className="flex-1 sm:flex-none h-12 px-7 text-sm font-bold text-white rounded-lg shadow-md hover:shadow-lg"
                        style={{ background: GREEN }}>
                  {approving ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <CheckCircle2 className="w-4 h-4 mr-2"/>}
                  Yes, activate {consent.student.full_name.split(' ')[0]}'s account
                </Button>
                <Button onClick={reject} variant="outline" data-testid="consent-reject-btn"
                        className="h-12 px-5 text-sm text-slate-600 border-slate-200 hover:bg-red-50 hover:border-red-200 hover:text-red-700">
                  <XCircle className="w-4 h-4 mr-2"/> Decline
                </Button>
              </div>

              {!detailsFilled && <p className="text-[11px] text-amber-700 mt-3 flex items-center gap-1.5"><AlertTriangle className="w-3 h-3"/> Enter your name, 10-digit phone number and email above.</p>}
            </div>

            {/* Footer */}
            <p className="text-[11px] text-slate-500 mt-6 text-center">
              Consent expires on {new Date(consent.expires_at).toLocaleString(undefined, { hour12: true })} · A copy of this approval will be e-mailed to {consent.parent.email}.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function FactRow({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
      <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="text-[13px] font-semibold text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}


function CenterLoader({ label }) {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: SOFT }}>
      <Loader label={label} />
    </div>
  );
}
function ErrorCard({ message }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: SOFT }}>
      <div className="max-w-md w-full bg-white border border-red-200 rounded-xl p-6 shadow text-center">
        <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2"/>
        <h3 className="text-base font-bold text-slate-900">Consent link unavailable</h3>
        <p className="text-sm text-slate-600 mt-1">{message}</p>
        <p className="text-[11px] text-slate-500 mt-3">If you received this link from your child's school, please contact them to re-issue it.</p>
      </div>
    </div>
  );
}
